
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import ConversationSidebar from "@/components/ConversationSidebar";
import ChatInterface from "@/components/ChatInterface";
import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const Chat = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { 
    currentConversation, 
    createNewConversation, 
    fetchMessages, 
    fetchConversations,
    conversations 
  } = useChat();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const initialLoadCompleted = useRef(false);
  const conversationCreationInProgress = useRef(false);
  const previousConversationId = useRef<string | undefined>(conversationId);
  const fetchInProgress = useRef(false);
  
  // 🔒 GUARDA DE ROTA: Persiste último conversationId válido
  const lastValidConversationId = useRef<string | null>(null);

  // Handle hash fragment for auth redirects
  useEffect(() => {
    if (location.hash && location.hash.includes('access_token')) {
      // Remove hash without page refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.hash, navigate, location.pathname]);

  // Detect conversation ID changes and fetch messages for the new conversation
  useEffect(() => {
    if (conversationId && conversationId !== previousConversationId.current && user && !fetchInProgress.current) {
      previousConversationId.current = conversationId;
      lastValidConversationId.current = conversationId; // 🔒 Salva último ID válido
      fetchInProgress.current = true;
      
      setIsLoading(true);
      fetchMessages(conversationId).finally(() => {
        setIsLoading(false);
        fetchInProgress.current = false;
      });
    }
  }, [conversationId, user, fetchMessages]);

  // Initial load effect - FIXED: removed fetchConversations from dependencies
  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    const loadData = async () => {
      try {
        if (initialLoadCompleted.current || fetchInProgress.current) {
          return;
        }

        fetchInProgress.current = true;
        setIsLoading(true);
        
        // Load all conversations first
        await fetchConversations();
        
        // If we have a specific conversation ID, fetch that conversation
        if (conversationId) {
          await fetchMessages(conversationId);
          initialLoadCompleted.current = true;
        } 
        // If conversations exist, navigate to the first one but only if we're on /chat without an ID
        else if (!conversationId && conversations.length > 0 && location.pathname === '/chat') {
          navigate(`/chat/${conversations[0].id}`, { replace: true });
          initialLoadCompleted.current = true;
        }
        // If no conversations exist, just show the empty state - don't create automatically
        else {
          initialLoadCompleted.current = true;
        }
      } catch (error) {
        console.error("Error in Chat component:", error);
        toast({
          title: "Erro",
          description: "Ocorreu um erro ao carregar as conversas. Por favor, tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        fetchInProgress.current = false;
      }
    };

    loadData();
  }, [conversationId, user, conversations.length, fetchMessages, navigate, location.pathname]);

  useEffect(() => {
    if (!isLoading && conversationId && conversations.length > 0 && 
        !conversations.some(c => c.id === conversationId)) {
      navigate('/chat', { replace: true });
    }
  }, [conversationId, conversations, isLoading, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 w-full">
      <ConversationSidebar />
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {isLoading || isCreatingConversation ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">
              {isCreatingConversation ? "Criando conversa..." : "Carregando..."}
            </span>
          </div>
        ) : (
          <ChatInterface />
        )}
      </main>
    </div>
  );
};

export default Chat;
